import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/GuidePage.css';

const GuidePage = () => {
  const { routeSlug } = useParams();
  const { t, i18n } = useTranslation();
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://villa-santeodoro.com';
  
  // Traductions fallback
  const languageCode = (i18n.resolvedLanguage || i18n.language || 'it').split('-')[0];
  
  const guideTextsFallback = {
    fr: {
      selectLanguage: 'Choisir une langue',
      loading: 'Chargement...',
      notFound: 'Guide introuvable',
      error: 'Une erreur provient du serveur, veuillez rafraîchir la page',
      downloadPDF: 'Télécharger le PDF'
    },
    it: {
      selectLanguage: 'Seleziona Lingua',
      loading: 'Caricamento...',
      notFound: 'Guida non trovata',
      error: 'Errore dal server, aggiorna la pagina',
      downloadPDF: 'Scarica PDF'
    },
    en: {
      selectLanguage: 'Select Language',
      loading: 'Loading...',
      notFound: 'Guide Not Found',
      error: 'Server error, please refresh',
      downloadPDF: 'Download PDF'
    },
    de: {
      selectLanguage: 'Sprache Wählen',
      loading: 'Laden...',
      notFound: 'Leitfaden Nicht Gefunden',
      error: 'Serverfehler, bitte aktualisieren Sie',
      downloadPDF: 'PDF Herunterladen'
    },
    es: {
      selectLanguage: 'Seleccionar Idioma',
      loading: 'Cargando...',
      notFound: 'Guía No Encontrada',
      error: 'Error del servidor, actualice',
      downloadPDF: 'Descargar PDF'
    },
    pt: {
      selectLanguage: 'Selecionar Idioma',
      loading: 'Carregando...',
      notFound: 'Guia Não Encontrada',
      error: 'Erro do servidor, atualize',
      downloadPDF: 'Baixar PDF'
    },
    ru: {
      selectLanguage: 'Выберите Язык',
      loading: 'Загрузка...',
      notFound: 'Руководство не найдено',
      error: 'Ошибка сервера, обновите',
      downloadPDF: 'Скачать PDF'
    }
  };
  
  const guideTexts = guideTextsFallback[languageCode] || guideTextsFallback.it;
  
  const [guide, setGuide] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfSrc, setPdfSrc] = useState(null);
  const [docsBaseUrl, setDocsBaseUrl] = useState(origin);
  const [notFound, setNotFound] = useState(false);
  const [guidesSourceUrl, setGuidesSourceUrl] = useState(null);
  const [guidesCount, setGuidesCount] = useState(null);

  const loadGuidesPayload = useCallback(async () => {
    const endpoints = [
      `${origin}/api/guides/config.php`,
      `${origin}/docs/guides.json`,
      `${origin}/public/docs/guides.json`,
      'https://villa-santeodoro.com/api/guides/config.php',
      'https://villa-santeodoro.com/docs/guides.json',
      'https://villa-santeodoro.com/public/docs/guides.json'
    ];

    let lastError = null;
    let emptyPayloadCandidate = null;

    for (const endpoint of endpoints) {
      try {
        const response = await fetch(endpoint, { cache: 'no-store' });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const contentType = response.headers.get('content-type') || '';
        if (!contentType.toLowerCase().includes('application/json')) {
          throw new Error('Réponse non JSON');
        }

        const payload = await response.json();
        const baseUrl = new URL(endpoint).origin;

        // Si on tombe sur un guides.json "vide" (ex: présent dans le build CRA mais non alimenté),
        // on continue d'essayer les autres endpoints avant d'abandonner.
        if (payload && Array.isArray(payload.guides) && payload.guides.length === 0) {
          if (!emptyPayloadCandidate) {
            emptyPayloadCandidate = { payload, baseUrl, endpoint };
          }
          continue;
        }

        return { payload, baseUrl, endpoint };
      } catch (err) {
        lastError = err;
      }
    }

    if (emptyPayloadCandidate) {
      return emptyPayloadCandidate;
    }

    throw lastError || new Error('Impossible de charger les guides');
  }, [origin]);

  // Charger la config des guides au montage
  useEffect(() => {
    let cancelled = false;

    const loadGuide = async () => {
      try {
        setLoading(true);
        const result = await loadGuidesPayload();
        const data = result?.payload;
        if (result?.baseUrl) {
          setDocsBaseUrl(result.baseUrl);
        }
        if (result?.endpoint) {
          setGuidesSourceUrl(result.endpoint);
        }
        if (data && Array.isArray(data.guides)) {
          setGuidesCount(data.guides.length);
        } else {
          setGuidesCount(null);
        }

        if (!data || !Array.isArray(data.guides)) {
          throw new Error(t('guide.error', { defaultValue: guideTexts.error }));
        }

        const targetSlug = String(routeSlug || '').toLowerCase();
        const foundGuide = data.guides.find((g) => String(g.id || g.slug || '').toLowerCase() === targetSlug);

        if (cancelled) {
          return;
        }

        if (!foundGuide) {
          setNotFound(true);
          setError(null);
          setGuide(null);
        } else {
          setNotFound(false);
          setGuide(foundGuide);

          // Sélectionner la langue de l'utilisateur si disponible, sinon la première
          const userLang = i18n.language?.split('-')[0];
          const availableLangs = Object.keys(foundGuide.languages || {}).filter(
            (lang) => foundGuide.languages?.[lang]
          );

          if (availableLangs.includes(userLang)) {
            setSelectedLanguage(userLang);
          } else if (availableLangs.length > 0) {
            setSelectedLanguage(availableLangs[0]);
          }

          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setNotFound(false);
          setError(t('guide.error', { defaultValue: guideTexts.error }));
          setGuide(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadGuide();

    return () => {
      cancelled = true;
    };
  }, [routeSlug, i18n.language, t, guideTexts.error, guideTexts.notFound, loadGuidesPayload]);

  const languages = ['fr', 'it', 'en', 'de', 'es', 'pt', 'ru'];
  const languageNames = {
    fr: 'Français',
    it: 'Italiano',
    en: 'English',
    de: 'Deutsch',
    es: 'Español',
    pt: 'Português',
    ru: 'Русский'
  };

  const availableLanguages = guide ? languages.filter((lang) => guide.languages[lang]) : [];
  const guideId = guide?.id || guide?.slug;
  const shouldUseProdPdfBase =
    typeof window !== 'undefined' &&
    (window.location.pathname.startsWith('/guides/') || window.location.pathname.startsWith('/guide/'));
  const pdfBaseUrl = shouldUseProdPdfBase ? 'https://villa-santeodoro.com' : docsBaseUrl;
  // Sur la prod (villa-santeodoro.com), les PDFs sont servis sous /public/docs.
  const pdfUrlLegacy = selectedLanguage && guideId ? `${pdfBaseUrl}/public/docs/${guideId}/${selectedLanguage}.pdf` : null;
  const pdfUrlApi =
    selectedLanguage && guideId
      ? `${pdfBaseUrl}/api/guides/pdf.php?guideId=${encodeURIComponent(guideId)}&lang=${encodeURIComponent(selectedLanguage)}`
      : null;
  const pdfUrl = selectedLanguage && guideId ? `${pdfBaseUrl}/docs/${guideId}/${selectedLanguage}.pdf` : null;

  const probePdfUrl = async (url) => {
    if (!url) {
      return false;
    }

    try {
      const headResponse = await fetch(url, { method: 'HEAD', cache: 'no-store' });
      if (headResponse.ok) {
        const ct = (headResponse.headers.get('content-type') || '').toLowerCase();
        return ct.includes('application/pdf') || ct.includes('application/octet-stream');
      }
    } catch (e) {
      // ignore
    }

    try {
      const rangeResponse = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
        headers: { Range: 'bytes=0-0' }
      });
      if (rangeResponse.ok || rangeResponse.status === 206) {
        const ct = (rangeResponse.headers.get('content-type') || '').toLowerCase();
        return ct.includes('application/pdf') || ct.includes('application/octet-stream');
      }
    } catch (e) {
      // ignore
    }

    return false;
  };

  useEffect(() => {
    let cancelled = false;

    const resolvePdfSrc = async () => {
      if (!selectedLanguage || !guideId) {
        setPdfSrc(null);
        return;
      }

      setPdfLoading(true);

      // On essaye d'abord /public/docs (prod), puis un fallback API (stream PDF), puis /docs.
      const candidates = [pdfUrlLegacy, pdfUrlApi, pdfUrl].filter(Boolean);
      for (const candidate of candidates) {
        // eslint-disable-next-line no-await-in-loop
        const ok = await probePdfUrl(candidate);
        if (cancelled) {
          return;
        }
        if (ok) {
          setPdfSrc(candidate);
          return;
        }
      }

      setPdfSrc(pdfUrlLegacy || pdfUrlApi || pdfUrl);
    };

    resolvePdfSrc();

    return () => {
      cancelled = true;
    };
  }, [selectedLanguage, guideId, pdfUrl, pdfUrlLegacy, pdfUrlApi]);

  if (loading) {
    return (
      <div className="guide-page loading-state">
        <p>{t('guide.loading', { defaultValue: guideTexts.loading })}</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="guide-page error-state">
        <h1>{t('guide.notFound', { defaultValue: guideTexts.notFound })}</h1>
        <p>Guide demandé : {routeSlug}</p>
        {guidesSourceUrl && (
          <p style={{ marginTop: '8px', wordBreak: 'break-all' }}>
            Source: {guidesSourceUrl}{typeof guidesCount === 'number' ? ` (guides: ${guidesCount})` : ''}
          </p>
        )}
        <a href="/" className="back-link">← Retour au site</a>
      </div>
    );
  }

  if (error || !guide) {
    return (
      <div className="guide-page error-state">
        <h1>{t('guide.notFound', { defaultValue: guideTexts.notFound })}</h1>
        <p>{error || t('guide.error', { defaultValue: guideTexts.error })}</p>
        <a href="/" className="back-link">← Retour au site</a>
      </div>
    );
  }

  return (
    <div className="guide-page">
      <div className="guide-header">
        <h1>{guide.label}</h1>
        <p className="guide-subtitle">{t('guide.selectLanguage', { defaultValue: guideTexts.selectLanguage })}</p>
      </div>

      {/* Sélecteur de langue */}
      <div className="language-selector">
        {availableLanguages.map(lang => (
          <button
            key={lang}
            className={`lang-button ${selectedLanguage === lang ? 'active' : ''}`}
            onClick={() => {
              setSelectedLanguage(lang);
            }}
          >
            {languageNames[lang]}
          </button>
        ))}
      </div>

      {/* Bouton de téléchargement au-dessus de la visionneuse */}
      {selectedLanguage && (pdfSrc || pdfUrlLegacy || pdfUrlApi || pdfUrl) && (
        <div className="pdf-fallback" style={{ marginBottom: '12px' }}>
          <p style={{ margin: 0 }}>
            Vous ne voyez pas la page ? Cliquez ici pour télécharger.
          </p>
          <a
            href={pdfSrc || pdfUrlLegacy || pdfUrlApi || pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="fallback-link"
          >
            {t('guide.downloadPDF', { defaultValue: guideTexts.downloadPDF })}
          </a>
        </div>
      )}

      {/* Affichage du PDF */}
      {selectedLanguage && pdfSrc && (
        <div className="pdf-viewer-container">
          {pdfLoading && (
            <div className="pdf-loading">{t('guide.loading', { defaultValue: guideTexts.loading })}</div>
          )}
          <iframe
            key={pdfSrc}
            src={`${pdfSrc}#toolbar=1&navpanes=0&scrollbar=1`}
            title={`${guide.label} - ${languageNames[selectedLanguage]}`}
            className={`pdf-iframe ${!pdfLoading ? 'loaded' : ''}`}
            onLoad={() => setPdfLoading(false)}
          />
        </div>
      )}
    </div>
  );
};

export default GuidePage;
